import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLists } from '../contexts/ListsContext';
import { useUserDesignations } from '../contexts/UserDesignationsContext';
import { useNotificationModal } from '../contexts/NotificationModalContext';
import { useTutorial } from '../contexts/TutorialContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { usePlaceAutocomplete } from '../hooks/usePlaceAutocomplete';
import { SelectedPlaceCard, DesignationSelect, AvoidReasonSelect } from '../components/AddPlaceModal/index';
import LimitReachedModal from '../components/LimitReachedModal';
import api from '../services/api';
import '../styles/AddPlacePage.css';
import '../styles/AddPlaceModal.css';

const INITIAL_FORM_DATA = {
  name: '',
  placeType: '',
  category: '',
  address: '',
  lat: null,
  lng: null,
  rating: 5,
  notes: '',
  designation: '',
  avoidReason: '',
  addressComponents: null,
  placeId: null,
  priceLevel: null,
  photoReference: null,
  googleMapsUrl: null,
  openingHours: null,
  businessStatus: null,
};

// Social links for users to link to their own content about this place
const SOCIAL_LINK_TYPES = [
  { value: 'tiktok', label: 'TikTok', placeholder: 'https://tiktok.com/@...', icon: 'tiktok' },
  { value: 'youtube', label: 'YouTube', placeholder: 'https://youtube.com/...', icon: 'youtube' },
  { value: 'instagram', label: 'Instagram', placeholder: 'https://instagram.com/...', icon: 'instagram' },
  { value: 'website', label: 'Website/Blog', placeholder: 'https://...', icon: 'globe' },
];

// Validate social link URL format and domain
const validateSocialLink = (type, url) => {
  if (!url || !url.trim()) return { valid: true }; // Empty is valid (optional)

  const trimmedUrl = url.trim();

  // Check valid URL format
  try {
    const parsed = new URL(trimmedUrl);
    if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
      return { valid: false, error: 'URL must start with http:// or https://' };
    }
  } catch {
    return { valid: false, error: 'Invalid URL format' };
  }

  // Check blocked domains (URL shorteners, etc.)
  const blockedPatterns = [/bit\.ly/i, /tinyurl\.com/i, /t\.co/i, /goo\.gl/i];
  if (blockedPatterns.some(p => p.test(trimmedUrl))) {
    return { valid: false, error: 'URL shorteners are not allowed' };
  }

  // Check domain for specific platforms
  const domainPatterns = {
    instagram: [/instagram\.com/i, /instagr\.am/i],
    tiktok: [/tiktok\.com/i],
    youtube: [/youtube\.com/i, /youtu\.be/i],
    website: null, // Any domain allowed
  };

  const patterns = domainPatterns[type];
  if (patterns !== null && !patterns.some(p => p.test(trimmedUrl))) {
    const typeLabel = SOCIAL_LINK_TYPES.find(t => t.value === type)?.label || type;
    return { valid: false, error: `URL must be a valid ${typeLabel} link` };
  }

  return { valid: true };
};

function AddPlace() {
  const { listId } = useParams();
  const navigate = useNavigate();
  const { lists, collaborationLists, addPlaceToList } = useLists();
  const { getDesignation, setDesignation: saveDesignation, ensureDesignationsLoaded } = useUserDesignations();
  const { showAlert, showToast } = useNotificationModal();
  const { isActive: isTutorialActive, notifyAction } = useTutorial();
  const { isPremium } = useSubscription();

  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [placeSelected, setPlaceSelected] = useState(false);
  const [duplicateError, setDuplicateError] = useState(null);
  const [existingPlaceInfo, setExistingPlaceInfo] = useState(null);
  const [autocompleteKey, setAutocompleteKey] = useState(0);
  const [saving, setSaving] = useState(false);
  const [limitModalOpen, setLimitModalOpen] = useState(false);
  const [limitModalLimit, setLimitModalLimit] = useState(30);
  const [isPrivate, setIsPrivate] = useState(false);
  const [socialLinks, setSocialLinks] = useState({});
  const [socialLinkErrors, setSocialLinkErrors] = useState({});

  // Combine owned and collaboration lists
  const allLists = useMemo(() => [...lists, ...collaborationLists], [lists, collaborationLists]);

  // Ensure designations are loaded for duplicate checking
  useEffect(() => {
    ensureDesignationsLoaded();
  }, [ensureDesignationsLoaded]);

  // Find the list
  const list = allLists.find(l => l.id === listId);
  const listName = list?.title || list?.name || 'List';


  // Helper function to check if a place exists on the current list or other lists
  const checkPlaceExistence = useCallback((googlePlaceId) => {
    setDuplicateError(null);
    setExistingPlaceInfo(null);

    if (!googlePlaceId || allLists.length === 0) return;

    // Check current list for duplicate
    const currentList = allLists.find(list => String(list.id) === String(listId));
    if (currentList && currentList.items) {
      const existsOnCurrentList = currentList.items.some(
        item => item.placeId === googlePlaceId
      );
      if (existsOnCurrentList) {
        setDuplicateError('This place is already on this list');
        return;
      }
    }

    // Check if user already has a designation for this place (global)
    const existingDesignation = getDesignation(googlePlaceId);
    if (existingDesignation) {
      // Check other lists for existing place (just to show where it's saved)
      const otherListsWithPlace = allLists
        .filter(list => String(list.id) !== String(listId))
        .filter(list => list.items && list.items.some(item => item.placeId === googlePlaceId))
        .map(list => ({
          listId: list.id,
          listName: list.title,
        }));

      setExistingPlaceInfo({
        lists: otherListsWithPlace,
        badge: existingDesignation.badge,
        avoidReason: existingDesignation.avoidReason
      });
      // Pre-fill the designation from existing user designation
      setFormData(prev => ({
        ...prev,
        designation: existingDesignation.badge || '',
        avoidReason: existingDesignation.avoidReason || '',
        notes: existingDesignation.notes || '',
      }));
      setIsPrivate(existingDesignation.isPrivate || false);

      // Initialize social links from existing designation
      const existingLinks = {};
      if (existingDesignation.socialLinks && Array.isArray(existingDesignation.socialLinks)) {
        existingDesignation.socialLinks.forEach(link => {
          if (link.type && link.url) {
            existingLinks[link.type] = link.url;
          }
        });
      }
      setSocialLinks(existingLinks);
      setSocialLinkErrors({});
    }
  }, [allLists, listId, getDesignation]);

  // Handle place selection from autocomplete
  const handlePlaceSelected = useCallback((placeData) => {
    setFormData((prevData) => ({
      ...prevData,
      ...placeData,
    }));
    setPlaceSelected(true);
    checkPlaceExistence(placeData.placeId);
  }, [checkPlaceExistence]);

  // Use autocomplete hook
  const {
    containerRef,
    clearInput,
    showDropdown,
    focusInput,
  } = usePlaceAutocomplete({
    isOpen: true,
    autocompleteKey,
    onPlaceSelected: handlePlaceSelected,
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleDesignationChange = (e) => {
    const designation = e.target.value;
    setFormData({
      ...formData,
      designation,
      avoidReason: designation === 'avoid' ? formData.avoidReason : ''
    });
  };

  const handleSocialLinkChange = (type, url) => {
    setSocialLinks(prev => ({
      ...prev,
      [type]: url,
    }));

    // Validate on change
    const validation = validateSocialLink(type, url);
    setSocialLinkErrors(prev => ({
      ...prev,
      [type]: validation.valid ? null : validation.error,
    }));
  };

  const validateAndCreatePlace = async () => {
    if (!placeSelected || !formData.name || !formData.address) {
      await showAlert('Please select a place from the search', { variant: 'warning' });
      return null;
    }

    if (duplicateError) {
      await showAlert(duplicateError, { variant: 'warning' });
      return null;
    }

    if (!formData.designation) {
      await showAlert('Please select a designation', { variant: 'warning' });
      return null;
    }

    if (formData.designation === 'avoid' && !formData.avoidReason) {
      await showAlert('Please select a reason to avoid', { variant: 'warning' });
      return null;
    }

    // Validate all social links
    const linkErrors = {};
    let hasLinkErrors = false;
    SOCIAL_LINK_TYPES.forEach(({ value: type }) => {
      const url = socialLinks[type];
      if (url && url.trim()) {
        const validation = validateSocialLink(type, url);
        if (!validation.valid) {
          linkErrors[type] = validation.error;
          hasLinkErrors = true;
        }
      }
    });

    if (hasLinkErrors) {
      setSocialLinkErrors(linkErrors);
      await showAlert('Please fix the social link errors', { variant: 'warning' });
      return null;
    }

    const now = new Date().toISOString();
    // Return place data for adding to list (no designation data - that's stored separately)
    return {
      id: Date.now(),
      name: formData.name,
      placeType: formData.placeType || null,
      category: formData.category || 'Activities',
      address: formData.address,
      lat: formData.lat || 0,
      lng: formData.lng || 0,
      addressComponents: formData.addressComponents,
      placeId: formData.placeId || null,
      priceLevel: formData.priceLevel !== null ? formData.priceLevel : null,
      photoUrl: formData.photoReference || null,
      photoUrlSavedAt: formData.photoReference ? now : null,
      googleMapsUrl: formData.googleMapsUrl || null,
      openingHours: formData.openingHours || null,
      openingHoursSavedAt: formData.openingHours ? now : null,
      businessStatus: formData.businessStatus || null,
      savedAt: now,
    };
  };

  const resetForm = () => {
    setFormData(INITIAL_FORM_DATA);
    setPlaceSelected(false);
    setDuplicateError(null);
    setExistingPlaceInfo(null);
    setIsPrivate(false);
    setSocialLinks({});
    setSocialLinkErrors({});
    clearInput();
    setAutocompleteKey(prev => prev + 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newPlace = await validateAndCreatePlace();
    if (!newPlace) return;

    setSaving(true);

    try {
      // Build social links array (only include non-empty links)
      const socialLinksArray = SOCIAL_LINK_TYPES
        .filter(({ value: type }) => socialLinks[type] && socialLinks[type].trim())
        .map(({ value: type }) => ({
          type,
          url: socialLinks[type].trim(),
          addedAt: new Date().toISOString(),
        }));

      // Save the user's designation (global, per-user per-place)
      await saveDesignation(formData.placeId, {
        badge: formData.designation,
        avoidReason: formData.designation === 'avoid' ? formData.avoidReason : null,
        notes: formData.notes || null,
        isPrivate,
        socialLinks: socialLinksArray.length > 0 ? socialLinksArray : null,
      });

      // Add place to the list
      await addPlaceToList(newPlace, listId);

      // For premium users, enrich the place with photos, opening hours, etc.
      // This is fire-and-forget - we don't block navigation on it
      // The enriched data will be available when the list is viewed
      if (isPremium && formData.placeId) {
        api.enrichPlace(formData.placeId).catch((error) => {
          console.error('Failed to enrich place (non-blocking):', error);
        });
      }

      // Notify tutorial that a place was added AND page is closing
      if (isTutorialActive) {
        notifyAction('placeAddedAndClosed', { placeName: newPlace.name });
      }

      navigate(`/list/${listId}`, { replace: true });
    } catch (error) {
      console.error('Failed to add place:', error);
      if (error.response?.data?.code === 'PLACE_LIMIT_REACHED' ||
          error.message?.includes('limit') ||
          error.code === 'PLACE_LIMIT_REACHED') {
        const limit = error.response?.data?.limit || error.limit || 30;
        setLimitModalLimit(limit);
        setLimitModalOpen(true);
      } else {
        showAlert(error.message || 'Failed to add place', { variant: 'error' });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleAddAndContinue = async (e) => {
    e.preventDefault();
    const newPlace = await validateAndCreatePlace();
    if (!newPlace) return;

    setSaving(true);

    try {
      // Build social links array (only include non-empty links)
      const socialLinksArray = SOCIAL_LINK_TYPES
        .filter(({ value: type }) => socialLinks[type] && socialLinks[type].trim())
        .map(({ value: type }) => ({
          type,
          url: socialLinks[type].trim(),
          addedAt: new Date().toISOString(),
        }));

      // Save the user's designation (global, per-user per-place)
      await saveDesignation(formData.placeId, {
        badge: formData.designation,
        avoidReason: formData.designation === 'avoid' ? formData.avoidReason : null,
        notes: formData.notes || null,
        isPrivate,
        socialLinks: socialLinksArray.length > 0 ? socialLinksArray : null,
      });

      // Add place to the list
      await addPlaceToList(newPlace, listId);

      // For premium users, enrich the place with photos, opening hours, etc.
      // This is fire-and-forget - we don't block form reset on it
      if (isPremium && formData.placeId) {
        api.enrichPlace(formData.placeId).catch((error) => {
          console.error('Failed to enrich place (non-blocking):', error);
        });
      }

      // Don't notify tutorial here - wait until page is closed
      resetForm();
      showToast('Place added! Add another one.');
    } catch (error) {
      console.error('Failed to add place:', error);
      if (error.response?.data?.code === 'PLACE_LIMIT_REACHED' ||
          error.message?.includes('limit') ||
          error.code === 'PLACE_LIMIT_REACHED') {
        const limit = error.response?.data?.limit || error.limit || 30;
        setLimitModalLimit(limit);
        setLimitModalOpen(true);
      } else {
        showAlert(error.message || 'Failed to add place', { variant: 'error' });
      }
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    navigate(`/list/${listId}`, { replace: true });
  };

  const handleClearPlace = () => {
    resetForm();
    showDropdown();
    focusInput();
  };

  return (
    <div className="add-place-page">
      <div className="add-place-content">
        <div className="form-header">
          <button className="back-btn" onClick={handleClose} aria-label="Go back">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <h1 className="form-title">Add Place</h1>
          <div className="header-spacer" />
        </div>

        <form onSubmit={handleSubmit} className="add-place-form">
          {/* 1. Place search/selection */}
          {!placeSelected && (
            <div className="form-group">
              <label>Search for a place</label>
              <div ref={containerRef} className="place-autocomplete-container" />
            </div>
          )}

          {placeSelected && (
            <>
              <SelectedPlaceCard
                formData={formData}
                duplicateError={duplicateError}
                existingPlaceInfo={existingPlaceInfo}
                onClear={handleClearPlace}
              />

              {/* 2. Designation */}
              <DesignationSelect
                designation={formData.designation}
                onChange={handleDesignationChange}
                existingPlaceInfo={existingPlaceInfo}
                duplicateError={duplicateError}
              />

              {formData.designation === 'avoid' && (
                <AvoidReasonSelect
                  avoidReason={formData.avoidReason}
                  onChange={handleInputChange}
                />
              )}

              {/* 3. Notes */}
              <div className="form-group">
                <label>Notes <span className="optional-label">(optional)</span></label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder="Add your thoughts, tips, or recommendations..."
                  className="form-textarea"
                  rows="4"
                />
              </div>

              {/* 4. Social Links */}
              <div className="form-group social-links-group">
                <label>Social Links <span className="optional-label">(optional)</span></label>
                <p className="form-hint">Link to your content about this place</p>
                <div className="social-links-list">
                  {SOCIAL_LINK_TYPES.map(({ value: type, label, placeholder, icon }) => (
                    <div key={type} className={`social-link-input ${socialLinkErrors[type] ? 'has-error' : ''}`}>
                      <div className="social-link-icon">
                        {icon === 'instagram' && (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                            <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                            <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
                            <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
                          </svg>
                        )}
                        {icon === 'tiktok' && (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                            <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                          </svg>
                        )}
                        {icon === 'youtube' && (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                            <path d="M22.54 6.42a2.78 2.78 0 0 0-1.94-2C18.88 4 12 4 12 4s-6.88 0-8.6.46a2.78 2.78 0 0 0-1.94 2A29 29 0 0 0 1 11.75a29 29 0 0 0 .46 5.33A2.78 2.78 0 0 0 3.4 19c1.72.46 8.6.46 8.6.46s6.88 0 8.6-.46a2.78 2.78 0 0 0 1.94-2 29 29 0 0 0 .46-5.25 29 29 0 0 0-.46-5.33z"/>
                            <polygon points="9.75 15.02 15.5 11.75 9.75 8.48 9.75 15.02"/>
                          </svg>
                        )}
                        {icon === 'globe' && (
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                            <circle cx="12" cy="12" r="10"/>
                            <line x1="2" y1="12" x2="22" y2="12"/>
                            <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                          </svg>
                        )}
                      </div>
                      <input
                        type="url"
                        className="form-input social-link-url"
                        value={socialLinks[type] || ''}
                        onChange={(e) => handleSocialLinkChange(type, e.target.value)}
                        placeholder={placeholder}
                      />
                      {socialLinks[type] && (
                        <button
                          type="button"
                          className="social-link-clear"
                          onClick={() => handleSocialLinkChange(type, '')}
                          aria-label={`Clear ${label}`}
                        >
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                            <line x1="18" y1="6" x2="6" y2="18"/>
                            <line x1="6" y1="6" x2="18" y2="18"/>
                          </svg>
                        </button>
                      )}
                      {socialLinkErrors[type] && (
                        <div className="social-link-error">{socialLinkErrors[type]}</div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* 5. Privacy toggle */}
              <div className="form-group privacy-toggle-group">
                <div className="privacy-toggle-row">
                  <div className="privacy-toggle-icon">
                    {isPrivate ? (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                        <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                      </svg>
                    ) : (
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                        <circle cx="12" cy="12" r="10"/>
                        <line x1="2" y1="12" x2="22" y2="12"/>
                        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
                      </svg>
                    )}
                  </div>
                  <span className="privacy-toggle-label-text">{isPrivate ? 'Private' : 'Public'}</span>
                  <label className="privacy-toggle-switch">
                    <input
                      type="checkbox"
                      checked={isPrivate}
                      onChange={(e) => setIsPrivate(e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
                <p className="privacy-toggle-description">
                  {isPrivate
                    ? 'Your designation, notes, and social links won\'t be visible to other users'
                    : 'Your designation, notes, and social links can be seen by other users'
                  }
                </p>
              </div>

              {/* 6. List indication */}
              <div className="list-indicator-group">
                <div className="list-indicator">
                  <span className="list-indicator-label">Adding to</span>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                    <path d="M3 6h18M3 12h18M3 18h18"/>
                  </svg>
                  <span className="list-indicator-name">{listName}</span>
                </div>
              </div>

              <div className="form-actions add-place-actions">
                <div className="btn-row">
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={handleAddAndContinue}
                    disabled={!!duplicateError || saving}
                  >
                    {saving ? 'Saving...' : 'Save & Add More'}
                  </button>
                  <button
                    type="submit"
                    className="btn-submit"
                    disabled={!!duplicateError || saving}
                  >
                    {saving ? 'Saving...' : 'Save & Close'}
                  </button>
                </div>
                <button type="button" className="btn-cancel" onClick={handleClose}>
                  Cancel
                </button>
              </div>
            </>
          )}
        </form>
      </div>

      <LimitReachedModal
        isOpen={limitModalOpen}
        onClose={() => setLimitModalOpen(false)}
        type="places"
        limit={limitModalLimit}
      />
    </div>
  );
}

export default AddPlace;
